/**
 * Last Light mode — nova destroys planets from innermost outward.
 * Pure functions — no side effects, no rendering.
 */

import type { PlanetState, PlayerState, ModeConfig, ModeState, LastLightState, WinResult } from '../types';
import { SeededRandom } from './random';
import {
  DEFAULT_DESTRUCTION_INTERVAL,
  DEFAULT_WARNING_TURNS,
  DEFAULT_FINAL_PLANETS_COUNT,
} from '../constants';

// =============================================================================
// SCHEDULE
// =============================================================================

/**
 * Build the ordered list of planet indices to be destroyed.
 * Stars and wormholes are excluded. Stops at totalPlanets - finalPlanetsCount.
 */
export function buildDestructionSchedule(
  planets: PlanetState[],
  config: ModeConfig,
  seed: string,
): number[] {
  const order = config.destructionOrder ?? 'innerFirst';
  const finalCount = config.finalPlanetsCount ?? DEFAULT_FINAL_PLANETS_COUNT;

  // Collect eligible planet indices (skip stars, wormholes)
  const eligible = planets
    .map((p, i) => ({ index: i, planet: p }))
    .filter(e => e.planet.type === 'planet' && e.planet.alive);

  const numToDestroy = Math.max(0, eligible.length - finalCount);

  switch (order) {
    case 'innerFirst': {
      const starPos = planets.find(p => p.type === 'star')?.position ?? { x: 0, y: 0, z: 0 };
      eligible.sort((a, b) => {
        const da = distSq(a.planet.position, starPos);
        const db = distSq(b.planet.position, starPos);
        return da - db;
      });
      return eligible.slice(0, numToDestroy).map(e => e.index);
    }
    case 'random':
    default: {
      const rng = new SeededRandom(`${seed}-lastlight-schedule`);
      const indices = eligible.map(e => e.index);
      // Fisher-Yates shuffle
      for (let i = indices.length - 1; i > 0; i--) {
        const j = rng.int(0, i);
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      return indices.slice(0, numToDestroy);
    }
  }
}

// =============================================================================
// INIT
// =============================================================================

export function initLastLightState(
  config: ModeConfig,
  planets: PlanetState[],
  seed: string,
): LastLightState {
  const schedule = buildDestructionSchedule(planets, config, seed);
  const interval = config.destructionInterval ?? DEFAULT_DESTRUCTION_INTERVAL;
  return {
    destroyedPlanets: [],
    schedule,
    nextDestroyTurn: interval,
  };
}

// =============================================================================
// TICK
// =============================================================================

export interface LastLightTickResult {
  updatedState: LastLightState;
  newlyDestroyed: number[];
}

/**
 * Advance the last-light layer for the given turn.
 * Returns updated state and list of newly destroyed planet indices.
 */
export function tickLastLight(
  state: LastLightState,
  turnNumber: number,
  config: ModeConfig,
): LastLightTickResult {
  const interval = config.destructionInterval ?? DEFAULT_DESTRUCTION_INTERVAL;

  if (turnNumber < state.nextDestroyTurn || state.schedule.length === 0) {
    return { updatedState: { ...state }, newlyDestroyed: [] };
  }

  // Destroy next planet from schedule
  const toDestroy = state.schedule[0];
  const remainingSchedule = state.schedule.slice(1);

  const destroyedPlanets = [...state.destroyedPlanets, toDestroy];

  return {
    updatedState: {
      ...state,
      destroyedPlanets,
      schedule: remainingSchedule,
      nextDestroyTurn: turnNumber + interval,
    },
    newlyDestroyed: [toDestroy],
  };
}

// =============================================================================
// WARNINGS
// =============================================================================

export interface DestructionWarning {
  planetIndex: number;
  turnsUntil: number;
}

/**
 * Get warnings for planets about to be destroyed.
 */
export function getDestructionWarnings(
  state: LastLightState | undefined,
  turnNumber: number,
  config: ModeConfig,
): DestructionWarning[] {
  if (!state) return [];

  const warningWindow = config.warningTurns ?? DEFAULT_WARNING_TURNS;
  const turnsUntilNext = state.nextDestroyTurn - turnNumber;

  if (turnsUntilNext > warningWindow || turnsUntilNext <= 0) return [];
  if (state.schedule.length === 0) return [];

  return [{ planetIndex: state.schedule[0], turnsUntil: turnsUntilNext }];
}

// =============================================================================
// PLANET DESTRUCTION
// =============================================================================

export interface DestroyPlanetResult {
  updatedPlanet: PlanetState;
  killedPlayerIds: number[];
}

/**
 * Destroy a planet — sets alive=false, kills all players on it.
 */
export function destroyPlanet(
  planet: PlanetState,
  players: PlayerState[],
  planetIndex: number = planet.id,  // players reference planets by ARRAY INDEX, not id
): DestroyPlanetResult {
  const updatedPlanet: PlanetState = { ...planet, alive: false };

  const killedPlayerIds: number[] = [];
  for (const p of players) {
    if (p.alive && p.planetIndex === planetIndex && !p.airborne) {
      killedPlayerIds.push(p.id);
    }
  }

  return { updatedPlanet, killedPlayerIds };
}

// =============================================================================
// WIN CONDITIONS
// =============================================================================

/**
 * Check if last light game is over.
 */
export function isLastLightGameOver(
  players: PlayerState[],
  modeState: ModeState,
  turnLimit?: number,
  turnNumber?: number,
): boolean {
  const aliveTeams = new Set(players.filter(p => p.alive).map(p => p.teamIndex));
  if (aliveTeams.size <= 1) return true;

  if (modeState.lastLight && modeState.lastLight.schedule.length === 0) {
    // All scheduled destructions complete — endgame
    return true;
  }

  if (turnLimit && turnNumber && turnNumber > turnLimit) return true;

  return false;
}

/**
 * Determine winner of last light mode. Deathmatch-style elimination.
 */
export function checkLastLightWin(
  players: PlayerState[],
  turnLimit?: number,
  turnNumber?: number,
): WinResult | null {
  const alivePlayers = players.filter(p => p.alive);
  if (alivePlayers.length === 0) return null;

  const aliveTeams = new Set(alivePlayers.map(p => p.teamIndex));

  // Elimination
  if (aliveTeams.size === 1) {
    const winningTeamIndex = alivePlayers[0].teamIndex;
    return {
      winningTeamIndex,
      winningPlayer: alivePlayers[0],
      reason: 'elimination',
    };
  }

  // Turn limit or schedule exhausted: score by survivors > kills > damage
  if ((turnLimit && turnNumber && turnNumber > turnLimit) || aliveTeams.size > 1) {
    return scoreDeathmatchStyle(players, alivePlayers);
  }

  return null;
}

function scoreDeathmatchStyle(
  players: PlayerState[],
  alivePlayers: PlayerState[],
): WinResult | null {
  const teamStats = new Map<number, { alive: number; kills: number; damage: number }>();

  for (const p of players) {
    const stats = teamStats.get(p.teamIndex) ?? { alive: 0, kills: 0, damage: 0 };
    if (p.alive) stats.alive++;
    stats.kills += p.kills;
    stats.damage += p.damageDealt;
    teamStats.set(p.teamIndex, stats);
  }

  const entries = [...teamStats.entries()];
  entries.sort((a, b) => {
    if (b[1].alive !== a[1].alive) return b[1].alive - a[1].alive;
    if (b[1].kills !== a[1].kills) return b[1].kills - a[1].kills;
    return b[1].damage - a[1].damage;
  });

  if (entries.length >= 2) {
    const [first, second] = entries;
    if (first[1].alive === second[1].alive &&
        first[1].kills === second[1].kills &&
        first[1].damage === second[1].damage) {
      return null;
    }
  }

  const winningTeamIndex = entries[0][0];
  const winningPlayer = alivePlayers.find(p => p.teamIndex === winningTeamIndex)
    ?? players.find(p => p.teamIndex === winningTeamIndex)!;

  const teamScores: Record<number, number> = {};
  for (const [teamIdx, stats] of entries) {
    teamScores[teamIdx] = stats.alive * 1000 + stats.kills * 10 + Math.round(stats.damage);
  }

  return {
    winningTeamIndex,
    winningPlayer,
    reason: 'turnLimit',
    teamScores,
  };
}

// =============================================================================
// HELPERS
// =============================================================================

function distSq(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
}
